// External libraries
import React from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";

// Project components
import Button from "../../../../Components/Button";
import Multiselect from "../../../../Components/Inputs/Multiselect";
import TextInput from "../../../../Components/Inputs/Text";
import Spacer from "../../../../Components/Spacer";
import { Text } from "../../../../Components/Text";

// Feature/context-specific hooks and constants
import { useConfiguration } from "../../contexts/ConfigurationContext";
import { useCustomizedData } from "../../contexts/CustomizedDataContext";

// Styles
import css from "../styles.styl";

// Local/relative feature components
import { GoalTable } from "./tables";

const GoalData = () => {
	const { showError, hideError, error } = useConfiguration();
	const { goalsData, updateGoalsData, addGoal, editGoal, deleteGoal, cancelEditGoal, employeeData } =
		useCustomizedData();

	const handleAddGoal = () => {
		if (!goalsData.goalName) {
			return showError("Goal name is required.");
		}
		if (!goalsData.goalAssignee || goalsData.goalAssignee.length === 0) {
			return showError("Goal assignee is required.");
		}
		if (!goalsData.goalOwner) {
			return showError("Goal owner is required.");
		}
		if (!goalsData.goalTarget) {
			return showError("Goal target is required.");
		}
		if (!goalsData.goalUnits) {
			return showError("Goal units is required.");
		}
		addGoal();
		hideError();
	};

	return (
		<div>
			{/* Goals Section */}
			<div>
				<Text variant="h2" weight="bold">
					{goalsData.editingGoalId ? "Edit Goal" : "Add Goals"}
				</Text>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Name
				</Text>
				<TextInput
					value={goalsData.goalName}
					onChange={(val) => {
						updateGoalsData({ goalName: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Description
				</Text>
				<TextInput
					value={goalsData.goalDescription}
					onChange={(val) => updateGoalsData({ goalDescription: val })}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Assignee
				</Text>
				{employeeData.employees.length === 0 ? (
					<>
						<Spacer size={1} />
						<Text variant="caption" weight="bold" style={{ color: "#666", fontStyle: "italic" }}>
							No employees found. Please add employees first.
						</Text>
						<Spacer size={1} />
					</>
				) : (
					<Multiselect
						options={employeeData.employees.map((s) => ({ label: s.name, id: s.id }))}
						value={goalsData.goalAssignee}
						onChange={(val) => {
							updateGoalsData({ goalAssignee: val });
							hideError();
						}}
						columns={4}
					/>
				)}
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Owner
				</Text>
				{employeeData.employees.length === 0 ? (
					<>
						<Spacer size={1} />
						<Text variant="caption" weight="bold" style={{ color: "#666", fontStyle: "italic" }}>
							No employees found. Please add employees first.
						</Text>
						<Spacer size={1} />
					</>
				) : (
					<Select
						options={employeeData.employees.map((s) => ({ label: s.name, id: s.id }))}
						value={goalsData.goalOwner}
						onChange={(val) => {
							updateGoalsData({ goalOwner: val });
							hideError();
						}}
					/>
				)}
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Target
				</Text>
				<TextInput
					value={goalsData.goalTarget}
					onChange={(val) => {
						updateGoalsData({ goalTarget: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Units
				</Text>
				<TextInput
					value={goalsData.goalUnits}
					onChange={(val) => {
						updateGoalsData({ goalUnits: val });
						hideError();
					}}
				/>
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Goal Value
				</Text>
				<TextInput value={goalsData.goalValue} onChange={(val) => updateGoalsData({ goalValue: val })} />
				<Spacer size={1} />
				<Text variant="body" weight="bold">
					Start Date
				</Text>
				<div className={css.inputContainer}>
					<DatePicker
						selected={goalsData.startDate}
						onChange={(val) => updateGoalsData({ startDate: val })}
					/>
				</div>
				<Spacer size={2} />
				<div style={{ display: "flex", gap: "10px" }}>
					<Button size="small" onClick={handleAddGoal}>
						{goalsData.editingGoalId ? "Update Goal" : "Add Goal"}
					</Button>
					{goalsData.editingGoalId && (
						<Button
							size="small"
							color="gray"
							onClick={() => {
								cancelEditGoal();
								hideError();
							}}
						>
							Cancel
						</Button>
					)}
				</div>
				{error &&
					(error.includes("Goal") ||
						error.includes("goal") ||
						error.includes("assignee") ||
						error.includes("owner") ||
						error.includes("target") ||
						error.includes("units")) && (
						<>
							<Spacer size={2} />
							<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
								<Text variant="h3" weight="bold" style={{ color: "red" }}>
									{error}
								</Text>
							</div>
							<Spacer size={1} />
						</>
					)}
				<Spacer size={2} />
				{goalsData.goals.length > 0 && (
					<GoalTable
						goals={goalsData.goals}
						employees={employeeData.employees}
						onEdit={editGoal}
						onDelete={deleteGoal}
					/>
				)}
			</div>
		</div>
	);
};

export default GoalData;
